import React, { useState, useEffect } from 'react';
import { IS_ADMIN_SURFACE } from '../../utils/surface';
import { Input, Select } from '../ui/Input';
import { PhoneInput } from '../ui/PhoneInput';
import { AddressAutocomplete } from '../ui/AddressAutocomplete';
import { Button } from '../ui/Button';
import { createCustomer } from '../../api/customers';
import { onCustomerCreated } from '../../api/billingHooks';
import { getIntegrators } from '../../api/integrators';
import { stripeApi } from '../../api/stripe';
import { apiCreate } from '../../api/apiClient';
import { US_JURISDICTIONS } from '../../constants/jurisdictions';

interface CustomerFormProps {
  defaultIntegratorId?: number;
  initialData?: any;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
}

const StepIndicator: React.FC<{ step: number; total: number }> = ({ step, total }) => (
  <div className="flex items-center justify-center mb-8" aria-label={`Step ${step} of ${total}`}>
    {Array.from({ length: total }, (_, i) => i + 1).map(s => (
      <React.Fragment key={s}>
        <div aria-current={s === step ? 'step' : undefined} className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${s === step ? 'bg-brand-600 text-white' : s < step ? 'bg-brand-100 text-brand-700' : 'bg-gray-200 text-gray-500'}`}>{s}</div>
        {s < total && <div className={`w-12 h-0.5 ${s < step ? 'bg-brand-400' : 'bg-gray-200'}`} />}
      </React.Fragment>
    ))}
  </div>
);

function generatePassword() {
  const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$';
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

export const CustomerForm: React.FC<CustomerFormProps> = ({ initialData, onSubmit, onCancel, defaultIntegratorId }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dealers, setDealers] = useState<any[]>([]);
  const [billingPortalUrl, setBillingPortalUrl] = useState('');
  const [createdData, setCreatedData] = useState<any>(null);

  const [companyName, setCompanyName] = useState(initialData?.company_name || '');
  const [integratorId, setIntegratorId] = useState(initialData?.dealer_id || initialData?.integrator_id || defaultIntegratorId || '');
  const [billingEmail, setBillingEmail] = useState(initialData?.billing_email || '');
  const [billingPhone, setBillingPhone] = useState(initialData?.billing_phone || '');
  const [billingContactName, setBillingContactName] = useState(initialData?.billing_contact_name || initialData?.bill_to || '');
  const [street, setStreet] = useState(initialData?.street || initialData?.address || '');
  const [city, setCity] = useState(initialData?.city || '');
  const [state, setState] = useState(initialData?.state || '');
  const [zip, setZip] = useState(initialData?.zip || '');
  const [billingStreet, setBillingStreet] = useState(initialData?.billing_address || '');
  const [billingCity, setBillingCity] = useState(initialData?.billing_city || '');
  const [billingState, setBillingState] = useState(initialData?.billing_state || '');
  const [billingZip, setBillingZip] = useState(initialData?.billing_zip || '');
  const [billingModel, setBillingModel] = useState(initialData?.billing_setting || '');
  const [sameBillingAddress, setSameBillingAddress] = useState(() => {
    if (!initialData) return true;
    const mailing = [initialData.address || '', initialData.city || '', initialData.state || '', initialData.zip || ''].join('|').toLowerCase();
    const billing = [initialData.billing_address || '', initialData.billing_city || '', initialData.billing_state || '', initialData.billing_zip || ''].join('|').toLowerCase();
    return !initialData.billing_address || mailing === billing;
  });
  const [adminFirst, setAdminFirst] = useState('');
  const [adminLast, setAdminLast] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState(generatePassword());
  const [autoGen, setAutoGen] = useState(true);

  useEffect(() => { getIntegrators().then((d: any) => setDealers(Array.isArray(d) ? d : d?.items || [])).catch(() => {}); }, []);

  // When the dealer changes (and the customer hasn't manually overridden billing model),
  // pull the dealer's default billing model so the customer inherits it visibly.
  useEffect(() => {
    if (initialData?.billing_setting) return; // edit mode with explicit value — leave alone
    const d = dealers.find((x: any) => String(x.id) === String(integratorId));
    if (d?.billing_setting) setBillingModel(d.billing_setting);
  }, [integratorId, dealers, initialData]);

  const validate = (s: number) => {
    const e: Record<string, string> = {};
    if (s === 1 && !companyName.trim()) e.companyName = 'Required';
    if (s === 2 && !billingEmail.trim()) e.billingEmail = 'Required';
    if (s === 3) {
      if (!adminFirst.trim()) e.adminFirst = 'Required';
      if (!adminLast.trim()) e.adminLast = 'Required';
      if (!adminEmail.trim()) e.adminEmail = 'Required';
      if (!adminPassword.trim()) e.adminPassword = 'Required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate(step)) setStep(s => s + 1); };
  const back = () => setStep(s => s - 1);

  // Pre-fill admin email from billing email when moving to step 3
  useEffect(() => { if (step === 3 && !adminEmail) setAdminEmail(billingEmail); }, [step]);

  const handleCreate = async () => {
    if (!validate(3)) return;
    setLoading(true);
    try {
      const billingAddress = [street, city, state, zip].filter(Boolean).join(', ');
      // 1. Create customer
      const customer = await createCustomer({
        name: companyName,
        billing_email: billingEmail,
        billing_phone: billingPhone,
        billing_address: billingAddress,
        dealer_id: integratorId || undefined,
        status: 'active',
      });

      // 2. Create admin user
      const user = await apiCreate('users', {
        email: adminEmail,
        password: adminPassword,
        name: `${adminFirst} ${adminLast}`.trim(),
        first_name: adminFirst,
        last_name: adminLast,
        role: 'customer',
        customer_id: customer.id,
        status: 'active',
      });

      // 3. Create Stripe customer
      const stripeId = await onCustomerCreated({
        id: customer.id,
        company_name: companyName,
        billing_email: billingEmail,
      });

      setCreatedData({ customer, user, stripeId });

      // 4. Get billing portal URL
      if (stripeId) {
        try {
          const portal = await stripeApi.createBillingPortal(stripeId, window.location.origin + '/billing');
          setBillingPortalUrl(portal.url);
        } catch { /* non-fatal */ }
      }

      setStep(4);
    } catch (err: any) {
      setErrors({ submit: err?.response?.data?.message || err.message || 'Failed to create customer' });
    } finally {
      setLoading(false);
    }
  };

  const copyUrl = () => { navigator.clipboard.writeText(billingPortalUrl); };

  const stepTitles = ['Company Info', 'Contact Info', 'Admin User Setup'];

  if (initialData) {
    const handleEditSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!companyName.trim()) { setErrors({ companyName: 'Required' }); return; }
      setLoading(true);
      try {
        const effectiveBillingStreet = sameBillingAddress ? street : billingStreet;
        const effectiveBillingCity = sameBillingAddress ? city : billingCity;
        const effectiveBillingState = sameBillingAddress ? state : billingState;
        const effectiveBillingZip = sameBillingAddress ? zip : billingZip;
        await onSubmit({
          name: companyName,
          company_name: companyName,
          address: street,
          city, state, zip,
          billing_email: billingEmail,
          billing_phone: billingPhone,
          billing_contact_name: billingContactName,
          billing_address: [effectiveBillingStreet, effectiveBillingCity, effectiveBillingState, effectiveBillingZip].filter(Boolean).join(', '),
          billing_city: effectiveBillingCity,
          billing_state: effectiveBillingState,
          billing_zip: effectiveBillingZip,
          billing_setting: billingModel || undefined,
          dealer_id: integratorId || null,
        });
      } catch (err: any) {
        setErrors({ submit: err?.response?.data?.message || err.message || 'Failed to update customer' });
      } finally {
        setLoading(false);
      }
    };
    return (
      <form onSubmit={handleEditSubmit}>
        {errors.submit && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{errors.submit}</div>}
        <Input label="Company Name *" value={companyName} onChange={e => setCompanyName(e.target.value)} error={errors.companyName} />
        <Select label="Dealer" value={integratorId} onChange={e => setIntegratorId(e.target.value)}>
          <option value="">— None —</option>
          {dealers.map((d: any) => <option key={d.id} value={d.id}>{d.company_name || d.name}</option>)}
        </Select>

        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-4 mb-2 pb-1 border-b border-gray-200">Mailing Address</h4>
        <AddressAutocomplete
          label="Street Address"
          value={street}
          onChange={setStreet}
          onSelect={(p) => { setStreet(p.street); if (p.city) setCity(p.city); if (p.state) setState(p.state); if (p.zip) setZip(p.zip); }}
        />
        <div className="grid grid-cols-3 gap-3">
          <Input label="City" value={city} onChange={e => setCity(e.target.value)} />
          <Select label="State" value={state} onChange={e => setState(e.target.value)}>
            <option value="">—</option>
            {US_JURISDICTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </Select>
          <Input label="ZIP" value={zip} onChange={e => setZip(e.target.value)} />
        </div>

        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-4 mb-2 pb-1 border-b border-gray-200">Billing Contact</h4>
        <Input label="Billing Contact Name" value={billingContactName} onChange={e => setBillingContactName(e.target.value)} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
          <Input label="Billing Email" type="email" value={billingEmail} onChange={e => setBillingEmail(e.target.value)} />
          <PhoneInput label="Billing Phone" value={billingPhone} onChange={setBillingPhone} />
        </div>
        <label className="flex items-center gap-2 mb-3 mt-1 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={sameBillingAddress}
            onChange={(e) => setSameBillingAddress(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          <span className="text-sm text-gray-700">Billing address is the same as the mailing address</span>
        </label>
        {!sameBillingAddress && (
          <>
            <AddressAutocomplete
              label="Billing Street Address"
              value={billingStreet}
              onChange={setBillingStreet}
              onSelect={(p) => { setBillingStreet(p.street); if (p.city) setBillingCity(p.city); if (p.state) setBillingState(p.state); if (p.zip) setBillingZip(p.zip); }}
            />
            <div className="grid grid-cols-3 gap-3">
              <Input label="Billing City" value={billingCity} onChange={e => setBillingCity(e.target.value)} />
              <Select label="Billing State" value={billingState} onChange={e => setBillingState(e.target.value)}>
                <option value="">—</option>
                {US_JURISDICTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </Select>
              <Input label="Billing ZIP" value={billingZip} onChange={e => setBillingZip(e.target.value)} />
            </div>
          </>
        )}

        {IS_ADMIN_SURFACE && (
          <>
            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-4 mb-2 pb-1 border-b border-gray-200">Billing Settings</h4>
            {(() => {
              const d = dealers.find((x: any) => String(x.id) === String(integratorId));
              const dealerDefault = d?.billing_setting;
              const labels: Record<string, string> = {
                dealer_billed: 'Intrlock bills dealer (less rebate)',
                customer_billed: 'Intrlock bills customer directly',
                property_billed: 'Intrlock bills property directly',
              };
              return (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Billing Model</label>
                  <select
                    value={billingModel}
                    onChange={e => setBillingModel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <option value="">{dealerDefault ? `Use dealer default — ${labels[dealerDefault] || dealerDefault}` : 'Use dealer default'}</option>
                    <option value="dealer_billed">Intrlock bills dealer (less rebate)</option>
                    <option value="customer_billed">Intrlock bills customer directly</option>
                    <option value="property_billed">Intrlock bills property directly</option>
                  </select>
                  {dealerDefault && billingModel && billingModel !== dealerDefault && (
                    <p className="text-xs text-amber-600 mt-1">This overrides the dealer's default ({labels[dealerDefault]}).</p>
                  )}
                </div>
              );
            })()}
          </>
        )}

        <div className="flex gap-3 justify-end mt-6">
          <Button variant="secondary" type="button" onClick={onCancel}>Cancel</Button>
          <Button type="submit" loading={loading}>Update Customer</Button>
        </div>
      </form>
    );
  }

  return (
    <div>
      {step <= 3 ? (
        <>
          <StepIndicator step={step} total={3} />
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Step {step} of 3</h3>
          <p className="text-sm text-gray-500 mb-6">{stepTitles[step - 1]}</p>
        </>
      ) : (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Customer created</h3>
          <p className="text-sm text-gray-500">Billing setup</p>
        </div>
      )}

      {errors.submit && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{errors.submit}</div>}

      {step === 1 && (
        <div>
          <Input label="Company Name *" value={companyName} onChange={e => setCompanyName(e.target.value)} error={errors.companyName} />
          <Select label="Dealer (optional)" value={integratorId} onChange={e => setIntegratorId(e.target.value)}>
            <option value="">— None —</option>
            {dealers.map((d: any) => <option key={d.id} value={d.id}>{d.company_name || d.name}</option>)}
          </Select>
        </div>
      )}

      {step === 2 && (
        <div>
          <Input label="Billing Email *" type="email" value={billingEmail} onChange={e => setBillingEmail(e.target.value)} error={errors.billingEmail} />
          <PhoneInput label="Billing Phone" value={billingPhone} onChange={setBillingPhone} />
          <AddressAutocomplete
            label="Street Address"
            value={street}
            onChange={setStreet}
            onSelect={(p) => { setStreet(p.street); if (p.city) setCity(p.city); if (p.state) setState(p.state); if (p.zip) setZip(p.zip); }}
          />
          <div className="grid grid-cols-3 gap-3">
            <Input label="City" value={city} onChange={e => setCity(e.target.value)} />
            <Select label="State" value={state} onChange={e => setState(e.target.value)}>
              <option value="">—</option>
              {US_JURISDICTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </Select>
            <Input label="ZIP" value={zip} onChange={e => setZip(e.target.value)} />
          </div>
        </div>
      )}

      {step === 3 && (
        <div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="First Name *" value={adminFirst} onChange={e => setAdminFirst(e.target.value)} error={errors.adminFirst} />
            <Input label="Last Name *" value={adminLast} onChange={e => setAdminLast(e.target.value)} error={errors.adminLast} />
          </div>
          <Input label="Admin Email *" type="email" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} error={errors.adminEmail} />
          <div className="flex items-center gap-3 mb-2">
            <label className="text-sm text-gray-600"><input type="checkbox" checked={autoGen} onChange={e => { setAutoGen(e.target.checked); if (e.target.checked) setAdminPassword(generatePassword()); }} className="mr-2" />Auto-generate password</label>
          </div>
          <Input label="Password *" value={adminPassword} onChange={e => setAdminPassword(e.target.value)} readOnly={autoGen} error={errors.adminPassword} />
        </div>
      )}

      {step === 4 && (
        <div>
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
            <p className="text-green-800 font-medium">✓ Customer created successfully!</p>
            <ul className="mt-2 text-sm text-green-700 space-y-1">
              <li>• Company: {companyName}</li>
              <li>• Admin user: {adminEmail}</li>
              {createdData?.stripeId && <li>• Stripe customer: {createdData.stripeId}</li>}
            </ul>
          </div>
          {billingPortalUrl ? (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm font-medium text-emerald-800 mb-2">Send this link to the customer to set up their payment method:</p>
              <div className="flex gap-2">
                <input readOnly value={billingPortalUrl} className="flex-1 px-3 py-2 bg-white border border-emerald-300 rounded text-sm text-emerald-900" />
                <Button size="sm" onClick={copyUrl}>Copy</Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Billing portal link unavailable.</p>
          )}
        </div>
      )}

      <div className="flex gap-3 justify-end mt-6">
        {step <= 3 && <Button variant="secondary" type="button" onClick={step === 1 ? onCancel : back}>{step === 1 ? 'Cancel' : 'Back'}</Button>}
        {step < 3 && <Button onClick={next}>Next</Button>}
        {step === 3 && <Button onClick={handleCreate} loading={loading}>Create Customer</Button>}
        {/* Success is no longer presented as a fourth creation step. */}
        {step === 4 && <Button onClick={onCancel}>Done</Button>}
      </div>
    </div>
  );
};
